import { useTranslation } from 'react-i18next';
import {
  QBadge,
  QBox,
  QButtonGroup,
  QCenter,
  QDivider,
  QHeading,
  QPropertiesPanel,
  QSpinner,
  QStack,
  QText,
  QTogglePropertiesPanelButton,
} from '@qualio/ui-components';
import styles from './EventFormProperties.module.css';
import { ManageApprovalUsers } from './ManageApprovalUsers/ManageApprovalUsers';
import { Link } from 'react-router-dom';
import { Approver, Form, QualityEvent } from '../../../types';
import { useUsers } from '../../../hooks';
import { useGroups } from '../../../hooks/useGroups';
import { useManageApprovalUsers } from '../../../hooks/useManageApprovalUsers';
import { useFilterApprovers } from '../../../hooks/useFilterApprovers';

type EventFormPropertiesProps = {
  companyId: number;
  stepId: number;
  event: QualityEvent;
  reviewers: Approver[];
  approvers: Approver[];
  form: Form;
  qualityApprovalRequired: boolean;
};

export const EventFormProperties = ({
  companyId,
  stepId,
  event,
  form,
  reviewers,
  approvers,
  qualityApprovalRequired,
}: EventFormPropertiesProps) => {
  const { t } = useTranslation();
  const { data: users, isLoading: isLoadingUsers } = useUsers();
  const { data: groups, isLoading: isLoadingGroups } = useGroups();
  const approvalUsers = useManageApprovalUsers(users ?? []);
  const filteredApprovers = useFilterApprovers(approvers);
  const allCurrentApprovers = [...filteredApprovers('quality'), ...filteredApprovers('other')];
  const showMergedApprovers = qualityApprovalRequired === false;

  if (isLoadingGroups || isLoadingUsers) {
    return (
      <QCenter>
        <QSpinner />
      </QCenter>
    );
  }

  return (
    <QPropertiesPanel openOnMount>
      <QStack className={styles['event-step-properties-variable-width']} fontSize="sm">
        <QStack
          marginTop={4}
          marginBottom={4}
          direction={'row'}
          justifyContent="space-between"
          spacing="8px"
          alignItems="center"
        >
          <QHeading fontSize="md">{t('common.labels.properties')}</QHeading>
          <QButtonGroup>
            <QTogglePropertiesPanelButton />
          </QButtonGroup>
        </QStack>
        <QStack spacing="16px">
          <QBox data-cy="assigned-to">
            <QText fontWeight="semibold">{t('common.labels.assignedTo')}</QText>
            <QText>{form.owner_full_name}</QText>
          </QBox>
          <QBox data-cy="event-title">
            <QText fontWeight="semibold">{t('common.labels.eventTitle')}</QText>
            <Link to={`/events/${event.id}`}>
              <QText>{event.title}</QText>
            </Link>
          </QBox>
          <QBox data-cy="event-code">
            <QText fontWeight="semibold">{t('common.labels.eventId')}</QText>
            <QBadge color="gray.500">{event.code}</QBadge>
          </QBox>
          <QDivider />
          {showMergedApprovers ? (
            <ManageApprovalUsers
              stepId={stepId}
              companyId={companyId}
              users={approvalUsers('mergedApprovers')}
              groups={groups ?? []}
              event={event}
              form={form}
              approvalUsers={allCurrentApprovers}
              allCurrentApprovers={allCurrentApprovers}
              qualityApprovalRequired={qualityApprovalRequired}
              approverType="mergedApprovers"
            />
          ) : (
            <>
              <ManageApprovalUsers
                stepId={stepId}
                companyId={companyId}
                users={approvalUsers('quality')}
                groups={groups ?? []}
                event={event}
                form={form}
                approvalUsers={filteredApprovers('quality')}
                allCurrentApprovers={allCurrentApprovers}
                qualityApprovalRequired={qualityApprovalRequired}
                approverType="quality"
              />
              <QDivider />
              <ManageApprovalUsers
                stepId={stepId}
                companyId={companyId}
                users={approvalUsers('other')}
                groups={groups ?? []}
                event={event}
                form={form}
                approvalUsers={filteredApprovers('other')}
                allCurrentApprovers={allCurrentApprovers}
                qualityApprovalRequired={qualityApprovalRequired}
                approverType="other"
              />
            </>
          )}
          <QDivider />
          <ManageApprovalUsers
            stepId={stepId}
            companyId={companyId}
            users={approvalUsers('reviewer')}
            groups={groups ?? []}
            event={event}
            form={form}
            approvalUsers={reviewers}
            approverType="reviewer"
          />
        </QStack>
      </QStack>
    </QPropertiesPanel>
  );
};
